import { useMutation } from '@apollo/client';
import {
    faDatabase,
    faEllipsisVertical,
    faHeadset,
    faInbox,
    faPhone,
    faRobot,
    faUser,
} from '@fortawesome/pro-regular-svg-icons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import AssistantCardShell, { AssistantCardMetaLine, AssistantCardStatus } from './AssistantCardShell.tsx';
import AvatarStack from '../../../core/components/AvatarStack.tsx';
import Button from '../../../core/components/Button.tsx';
import ConfirmModal from '../../../core/components/ConfirmModal.tsx';
import DropdownMenu from '../../../core/components/DropdownMenu.tsx';
import { withIcon } from '../../../core/components/Icon.tsx';
import { useToaster } from '../../../core/components/Toast.tsx';
import { useApplicationContext } from '../../../core/contexts/ApplicationContext.tsx';
import { canManageAgents, canManageVoiceAgents } from '../../../users/utils.ts';
import VoiceAgentTestModal from '../../voice_agents/components/VoiceAgentTestModal.tsx';
import { DELETE_VOICE_AGENT, GET_VOICE_AGENTS, SET_VOICE_AGENT_ENABLED } from '../../voice_agents/queries.ts';
import { DELETE_AGENT, GET_AGENTS, SET_AGENT_ENABLED } from '../queries.ts';
import { AssistantCardData } from '../utils/adaptAssistant.ts';

const RobotIcon = withIcon(faRobot);
const HeadsetIcon = withIcon(faHeadset);
const PhoneIcon = withIcon(faPhone);
const DatabaseIcon = withIcon(faDatabase);
const MailIcon = withIcon(faInbox);
const PersonIcon = withIcon(faUser);
const EllipsisIcon = withIcon(faEllipsisVertical);

const AssistantCard = ({ assistant, onChanged }: { assistant: AssistantCardData; onChanged: () => void }) => {
    const { t } = useTranslation(['assistants', 'voice_agents']);
    const { user } = useApplicationContext();
    const { publishToast } = useToaster();
    const navigate = useNavigate();

    const [isDeleting, setIsDeleting] = useState(false);
    const [isTesting, setIsTesting] = useState(false);

    const [setAgentEnabled, { loading: agentPauseLoading }] = useMutation(SET_AGENT_ENABLED);
    const [deleteAgent, { loading: agentDeleteLoading }] = useMutation(DELETE_AGENT, {
        refetchQueries: [{ query: GET_AGENTS }, 'GetNavigationAgents'],
    });
    const [setVoiceAgentEnabled, { loading: voiceAgentPauseLoading }] = useMutation(SET_VOICE_AGENT_ENABLED);
    const [deleteVoiceAgent, { loading: voiceAgentDeleteLoading }] = useMutation(DELETE_VOICE_AGENT, {
        refetchQueries: [{ query: GET_VOICE_AGENTS }, 'GetNavigationAgents'],
    });

    const isVoiceAgent = assistant.kind === 'voiceAgent';
    const setEnabled = isVoiceAgent ? setVoiceAgentEnabled : setAgentEnabled;
    const deleteAssistant = isVoiceAgent ? deleteVoiceAgent : deleteAgent;
    const pauseLoading = isVoiceAgent ? voiceAgentPauseLoading : agentPauseLoading;
    const deleteLoading = isVoiceAgent ? voiceAgentDeleteLoading : agentDeleteLoading;
    const isManager = isVoiceAgent ? canManageVoiceAgents(user) : canManageAgents(user);

    const TileIcon = isVoiceAgent ? HeadsetIcon : RobotIcon;

    const handleStatusChange = async (action: 'pause' | 'resume' | 'delete') => {
        if (action === 'delete') {
            setIsDeleting(true);
            return;
        }
        try {
            await setEnabled({ variables: { id: assistant.id, enabled: action === 'resume' } });
            publishToast({
                description: t(action === 'resume' ? 'overview.resumeSuccess' : 'overview.pauseSuccess'),
                status: 'success',
            });
            onChanged();
        } catch {
            publishToast({
                description: t(action === 'resume' ? 'overview.resumeError' : 'overview.pauseError'),
                status: 'error',
            });
        }
    };

    const handleDeleteConfirm = async () => {
        try {
            await deleteAssistant({ variables: { id: assistant.id } });
            publishToast({ description: t('overview.deleteSuccess'), status: 'success' });
        } catch {
            publishToast({ description: t('overview.deleteError'), status: 'error' });
        }
        setIsDeleting(false);
    };

    const menu = isManager ? (
        <div onClick={(e) => e.preventDefault()} className="flex flex-none items-center">
            <DropdownMenu>
                <DropdownMenu.Trigger asChild>
                    <Button
                        type="button"
                        variant="ghost"
                        aria-label={t('overview.actionMore')}
                        className="flex h-8 w-8 items-center justify-center px-0 py-0 opacity-0 focus-visible:opacity-100 group-hover:opacity-100 group-focus-visible:opacity-100 data-[state=open]:opacity-100"
                        disabled={pauseLoading || deleteLoading}
                    >
                        <EllipsisIcon />
                    </Button>
                </DropdownMenu.Trigger>
                <DropdownMenu.Content align="end" className="min-w-[10rem]">
                    {isVoiceAgent && (
                        <>
                            <DropdownMenu.Item
                                className="px-3 py-2.5 text-sm"
                                onClick={() => navigate(assistant.configPath)}
                            >
                                {t('overview.actionManage')}
                            </DropdownMenu.Item>
                            {assistant.testLink && (
                                <DropdownMenu.Item className="px-3 py-2.5 text-sm" onClick={() => setIsTesting(true)}>
                                    {t('voice_agents:overview.actionTest')}
                                </DropdownMenu.Item>
                            )}
                            <DropdownMenu.Separator />
                        </>
                    )}
                    {assistant.enabled ? (
                        <DropdownMenu.Item className="px-3 py-2.5 text-sm" onClick={() => handleStatusChange('pause')}>
                            {t('overview.actionPause')}
                        </DropdownMenu.Item>
                    ) : (
                        <DropdownMenu.Item className="px-3 py-2.5 text-sm" onClick={() => handleStatusChange('resume')}>
                            {t('overview.actionResume')}
                        </DropdownMenu.Item>
                    )}
                    <DropdownMenu.Item
                        onClick={() => handleStatusChange('delete')}
                        className="px-3 py-2.5 text-sm"
                        danger
                    >
                        {t('overview.actionDelete')}
                    </DropdownMenu.Item>
                </DropdownMenu.Content>
            </DropdownMenu>
        </div>
    ) : null;

    const hasMailboxOwners = assistant.mailboxOwners.length > 0;
    const mailboxOwnersStack = hasMailboxOwners && <AvatarStack people={assistant.mailboxOwners} />;
    const meta: AssistantCardMetaLine[] = [
        assistant.phoneLabel && { icon: <PhoneIcon />, label: assistant.phoneLabel },
        assistant.dataSourceLabel && { icon: <DatabaseIcon />, label: assistant.dataSourceLabel },
        assistant.mailboxes.length > 0
            ? {
                  icon: <MailIcon />,
                  label: assistant.mailboxes.join(', '),
                  trailing: hasMailboxOwners && (
                      <>
                          <PersonIcon />
                          {mailboxOwnersStack}
                      </>
                  ),
              }
            : hasMailboxOwners && { icon: <PersonIcon />, trailing: mailboxOwnersStack },
    ].filter(Boolean);

    return (
        <>
            <AssistantCardShell
                to={assistant.detailPath}
                icon={<TileIcon />}
                title={assistant.name}
                subtitle={assistant.subtitle}
                status={!assistant.enabled && <AssistantCardStatus tone="warning" label={t('overview.statusPaused')} />}
                addon={menu}
                chips={assistant.chips}
                meta={meta}
            />

            {isTesting && assistant.testLink && (
                <VoiceAgentTestModal agentLink={assistant.testLink} onClose={() => setIsTesting(false)} />
            )}

            {isDeleting && (
                <ConfirmModal
                    onConfirm={handleDeleteConfirm}
                    onCancel={() => setIsDeleting(false)}
                    title={t('overview.deleteConfirmTitle')}
                    labelConfirm={t('overview.delete')}
                    confirmDisabled={deleteLoading}
                    danger
                >
                    {t('overview.deleteConfirmMessage', { name: assistant.name })}
                </ConfirmModal>
            )}
        </>
    );
};

export default AssistantCard;
